import { useState } from 'react';
import { CheckCircleOutlined, IdcardOutlined, StopOutlined } from '@ant-design/icons';

import { PageHeader } from '../../../components/shared';
import { Badge, Button, Card, CardBody, Field, Input, Select } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { LOAI_GIAY_TO, QUYEN, TEN_HANG_MUC, THU_TU_HANG_MUC } from '../constants';
import { ChupAnh } from '../components/ChupAnh';
import { useDsQuyTrinh, useDsUngDung } from '../hooks/useCauHinhEkyc';
import { useBatDauDinhDanh, useKetThucPhien, useTaiAnh } from '../hooks/useEkyc';
import type { AnhDaTai, LoaiGiayTo, PhienTomTat, QuyTrinh, UngDung, VaiTroTaiLen } from '../types';

/** Loại giấy tờ không có mặt sau (04_HANG_MUC_KIEM_TRA.md §12.1). Chép từ `KHONG_CAN_MAT_SAU` của v3. */
const KHONG_CAN_MAT_SAU: readonly LoaiGiayTo[] = ['VIETNAM_PASSPORT'];

interface OAnh {
  vaiTro: VaiTroTaiLen;
  ten: KhoaDich;
  mo: KhoaDich;
  /** Camera trước cho chân dung, camera sau cho giấy tờ — gợi ý cho điện thoại, máy tính bỏ qua. */
  camera: 'user' | 'environment';
}

/**
 * Bước chụp nào đang bật theo quy trình đã chọn. Không đọc được quy trình (thiếu quyền `EKYC_WORKFLOW.READ`) thì theo
 * mặc định của hệ thống — cả hai bật — đúng như máy chủ sẽ chụp vào phiên.
 */
interface BuocChup {
  matSau: boolean;
  chanDung: boolean;
}
const BUOC_CHUP_MAC_DINH: BuocChup = { matSau: true, chanDung: true };

/** Khớp `anhCanCo` của máy chủ (`ho-so/anh-can-co.ts`): lượt nộp đòi đúng bộ ảnh này. */
function oAnhCua(loai: LoaiGiayTo, bc: BuocChup): OAnh[] {
  const o: OAnh[] = [
    { vaiTro: 'DOCUMENT_FRONT', ten: 'ekyc.dinhDanh.buocMatTruoc', mo: 'ekyc.dinhDanh.buocMatTruocMo', camera: 'environment' },
  ];
  if (bc.matSau && !KHONG_CAN_MAT_SAU.includes(loai)) {
    o.push({ vaiTro: 'DOCUMENT_BACK', ten: 'ekyc.dinhDanh.buocMatSau', mo: 'ekyc.dinhDanh.buocMatSauMo', camera: 'environment' });
  }
  if (bc.chanDung) {
    o.push({ vaiTro: 'SELFIE', ten: 'ekyc.dinhDanh.buocChanDung', mo: 'ekyc.dinhDanh.buocChanDungMo', camera: 'user' });
  }
  return o;
}

/**
 * Định danh người dùng (`/ekyc/dinh-danh`, phân hệ `EKYC_SESSION`) — định danh khách **đang đứng tại quầy**.
 *
 * Dựng theo giao diện v3 (ảnh người dùng cung cấp 24/10/2026): chọn loại giấy tờ ở đầu màn, khối "Luồng định
 * danh sẽ chạy", rồi khối đồng ý. Đồng ý xong thì chụp ảnh và nộp, cùng một màn.
 *
 * Ba điều cố ý, mỗi điều gắn với một bất biến của bộ bàn giao:
 *
 * 1. **Khách tự bấm đồng ý, trên chính màn này.** Đồng ý là hành vi của người dùng cuối (02_API.md §7.1); nhân
 *    viên bấm hộ thì bản ghi đồng ý mất giá trị bằng chứng. Màn nói thẳng điều đó ngay trên hai nút.
 * 2. **"Tôi không đồng ý" cũng được ghi lại.** Một bảng chỉ ghi lượt đồng ý không trả lời được câu hỏi "người
 *    này đã từng được hỏi chưa" — đúng câu sẽ được đặt khi có khiếu nại.
 * 3. **Không hứa điều chưa có.** Các bước tự động (OCR, đối chiếu khuôn mặt…) chưa chạy ở bản này; khối "Hệ thống
 *    tự chạy" nói rõ như vậy thay vì vẽ chúng như thể đang kiểm. Một bước an ninh trông như đang bật mà không chạy
 *    là điều tệ hơn hẳn một bước tắt (08_LO_TRINH_V4.md §6).
 */
export function DinhDanhView() {
  const { t } = useT();
  const dichLoi = useLoi();
  const toast = useToast();
  const dieuHuong = useDieuHuong();
  const quyen = useModulePermissions(QUYEN);

  const [loai, setLoai] = useState<LoaiGiayTo>('VIETNAM_CCCD');
  const [maHoSo, setMaHoSo] = useState('');
  const [phien, setPhien] = useState<PhienTomTat | null>(null);
  const [anh, setAnh] = useState<Partial<Record<VaiTroTaiLen, AnhDaTai>>>({});
  const [hoiHuy, setHoiHuy] = useState(false);

  const batDau = useBatDauDinhDanh();

  // Ứng dụng và quy trình: chỉ hiện khi đọc được; không đọc được thì máy chủ dùng ứng dụng và quy trình mặc định.
  const dsUngDung = useDsUngDung(quyen.ungDungDoc);
  const [ungDungChon, setUngDungChon] = useState<string | null>(null);
  const ungDungId = ungDungChon ?? dsUngDung.data?.items.find((u) => u.dangBat)?.id ?? null;
  const dsQuyTrinh = useDsQuyTrinh(quyen.quyTrinhDoc ? ungDungId : null);
  const [quyTrinhChon, setQuyTrinhChon] = useState<string | null>(null);
  // Quy trình không còn hợp lệ hay đang tắt thì máy chủ từ chối mở phiên — không đưa vào danh sách chọn.
  const dsQuyTrinhDung = (dsQuyTrinh.data?.items ?? []).filter((q) => q.dangBat && !q.vanDe.some((v) => v.muc === 'loi'));
  const qtDung = dsQuyTrinhDung.find((q) => q.id === quyTrinhChon) ?? dsQuyTrinhDung.find((q) => q.macDinh) ?? null;
  const batCua = (ma: string) => qtDung?.effectiveSteps.find((b) => b.ma === ma)?.bat ?? true;
  const bc: BuocChup = qtDung ? { matSau: batCua('mat_sau'), chanDung: batCua('chan_dung') } : BUOC_CHUP_MAC_DINH;
  const tieuDe = t('ekyc.dinhDanh.tieuDe');

  const lamLai = () => {
    setPhien(null);
    setAnh({});
    setMaHoSo('');
    batDau.reset();
  };

  const traLoi = (dongY: boolean) =>
    batDau.mutate({
      documentType: loai,
      referenceId: maHoSo.trim(),
      dongY,
      ...(ungDungId && quyen.ungDungDoc ? { ungDungId } : {}),
      // Chỉ gửi `workflowId` khi khác mặc định: chọn đích danh đòi thêm quyền đọc quy trình ở máy chủ.
      ...(qtDung && !qtDung.macDinh ? { workflowId: qtDung.id } : {}),
    }, { onSuccess: setPhien });

  // Loại giấy tờ chỉ chọn được trước khi mở phiên: đổi giữa chừng là bộ ảnh đã chụp không còn khớp loại đã khai.
  const daMoPhien = phien !== null;

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[{ label: t('nav.nhom.dinhDanhEkyc') }, { label: tieuDe }]}
          titleIcon={<IdcardOutlined className="text-primary-600" />}
          title={tieuDe}
          subtitle={t('ekyc.dinhDanh.moTa')}
          actions={
            <Select<LoaiGiayTo>
              value={loai}
              onChange={setLoai}
              disabled={daMoPhien}
              aria-label={t('ekyc.dinhDanh.loaiGiayTo')}
              className="w-64"
              options={Object.keys(LOAI_GIAY_TO).map((k) => ({ value: k as LoaiGiayTo, label: t(LOAI_GIAY_TO[k]!) }))}
            />
          }
        />

        <div className="mx-auto w-full max-w-3xl space-y-4">
          {(quyen.ungDungDoc || quyen.quyTrinhDoc) && phien === null && (
            <ChonQuyTrinh
              ungDungId={ungDungId}
              onUngDung={(id) => { setUngDungChon(id); setQuyTrinhChon(null); }}
              quyTrinhId={qtDung?.id ?? null}
              onQuyTrinh={setQuyTrinhChon}
              dsUngDung={dsUngDung.data?.items.filter((u) => u.dangBat) ?? []}
              dsQuyTrinh={dsQuyTrinhDung}
              hienQuyTrinh={quyen.quyTrinhDoc}
            />
          )}
          <KhoiLuong loai={loai} bc={bc} />

          {!quyen.phienTao ? (
            <Card>
              <CardBody>
                <p className="text-sm text-content-secondary">{t('ekyc.dinhDanh.khongQuyenTao')}</p>
              </CardBody>
            </Card>
          ) : phien === null ? (
            <Card>
              <CardBody className="space-y-4">
                <Field label={t('ekyc.dinhDanh.maHoSo')} hint={t('ekyc.dinhDanh.maHoSoMo')}>
                  {(p) => (
                    <Input
                      {...p}
                      value={maHoSo}
                      maxLength={128}
                      onChange={(e) => setMaHoSo(e.target.value)}
                      placeholder={t('ekyc.dinhDanh.maHoSoGoiY')}
                    />
                  )}
                </Field>

                <div className="space-y-2 border-t border-default pt-4">
                  <h2 className="text-sm font-semibold text-content-primary">{t('ekyc.dinhDanh.dongYTieuDe')}</h2>
                  <p className="text-sm text-content-secondary">{t('ekyc.dinhDanh.dongYMo')}</p>
                  <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-content-primary">
                    <li>{t('ekyc.dinhDanh.dongYY1')}</li>
                    <li>{t('ekyc.dinhDanh.dongYY2')}</li>
                    <li>{t('ekyc.dinhDanh.dongYY3')}</li>
                  </ul>
                </div>

                <p className="text-xs text-content-muted">{t('ekyc.dinhDanh.khachTuBam')}</p>
                {batDau.isError && (
                  <p role="alert" className="text-sm text-danger-700">{dichLoi(batDau.error)}</p>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="primary"
                    size="md"
                    icon={<CheckCircleOutlined />}
                    disabled={batDau.isPending}
                    onClick={() => traLoi(true)}
                  >
                    {batDau.isPending ? t('ekyc.dinhDanh.dangGhiNhan') : t('ekyc.dinhDanh.dongYNut')}
                  </Button>
                  <Button variant="outline" size="md" disabled={batDau.isPending} onClick={() => traLoi(false)}>
                    {t('ekyc.dinhDanh.khongDongYNut')}
                  </Button>
                </div>
              </CardBody>
            </Card>
          ) : phien.status === 'CANCELLED' ? (
            <Card>
              <CardBody className="space-y-3">
                <div className="flex items-start gap-2">
                  <StopOutlined className="mt-0.5 shrink-0 text-content-muted" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-content-primary">{t('ekyc.dinhDanh.tuChoiTieuDe')}</p>
                    <p className="text-sm text-content-secondary">{t('ekyc.dinhDanh.tuChoiMo')}</p>
                  </div>
                </div>
                <Button variant="outline" size="md" onClick={lamLai}>{t('ekyc.dinhDanh.dinhDanhKhac')}</Button>
              </CardBody>
            </Card>
          ) : (
            <KhoiAnh
              phien={phien}
              loai={loai}
              bc={bc}
              anh={anh}
              onCoAnh={(a) => setAnh((cu) => ({ ...cu, [a.role]: a }))}
              onNop={(p) => {
                toast.success(t('ekyc.dinhDanh.daNop'));
                dieuHuong(`/ekyc/ho-so/${p.id}`);
              }}
              onHoiHuy={() => setHoiHuy(true)}
            />
          )}
        </div>
      </div>

      {phien && (
        <HopHuy
          phienId={phien.id}
          open={hoiHuy}
          onClose={() => setHoiHuy(false)}
          onDaHuy={() => {
            setHoiHuy(false);
            toast.success(t('ekyc.dinhDanh.daHuy'));
            lamLai();
          }}
        />
      )}
    </div>
  );
}

/** Khối "Luồng định danh sẽ chạy": việc của người, và việc của máy — nói thật là máy chưa chạy. */
function KhoiLuong({ loai, bc }: { loai: LoaiGiayTo; bc: BuocChup }) {
  const { t } = useT();
  const buoc: { ten: KhoaDich; mo: KhoaDich }[] = [
    { ten: 'ekyc.dinhDanh.buocDongY', mo: 'ekyc.dinhDanh.buocDongYMo' },
    ...oAnhCua(loai, bc).map((o) => ({ ten: o.ten, mo: o.mo })),
  ];
  return (
    <Card>
      <CardBody className="space-y-4">
        <h2 className="text-sm font-semibold text-content-primary">{t('ekyc.dinhDanh.luong')}</h2>
        <div>
          <p className="mb-2 text-2xs font-semibold uppercase tracking-wide text-content-muted">
            {t('ekyc.dinhDanh.banThucHien')}
          </p>
          <ol className="space-y-2">
            {buoc.map((b, i) => (
              <li key={b.ten} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-50 text-xs font-medium text-primary-700">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-content-primary">{t(b.ten)}</p>
                  <p className="text-xs text-content-secondary">{t(b.mo)}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="border-t border-default pt-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <p className="text-2xs font-semibold uppercase tracking-wide text-content-muted">
              {t('ekyc.dinhDanh.heThongTuChay')}
            </p>
            <Badge variant="neutral">{t('ekyc.dinhDanh.chuaChay')}</Badge>
          </div>
          <ul className="flex flex-wrap gap-2">
            {THU_TU_HANG_MUC.map((hm) => (
              <li key={hm}>
                <Badge variant="neutral">{t(TEN_HANG_MUC[hm])}</Badge>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-content-secondary">{t('ekyc.dinhDanh.chuaChayMo')}</p>
        </div>
      </CardBody>
    </Card>
  );
}

function KhoiAnh({
  phien,
  loai,
  bc,
  anh,
  onCoAnh,
  onNop,
  onHoiHuy,
}: {
  phien: PhienTomTat;
  loai: LoaiGiayTo;
  bc: BuocChup;
  anh: Partial<Record<VaiTroTaiLen, AnhDaTai>>;
  onCoAnh: (a: AnhDaTai) => void;
  onNop: (p: PhienTomTat) => void;
  onHoiHuy: () => void;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const { nop } = useKetThucPhien(phien.id);
  const o = oAnhCua(loai, bc);
  const thieu = o.filter((x) => !anh[x.vaiTro]).length;

  return (
    <Card>
      <CardBody className="space-y-4">
        <div>
          <h2 className="text-sm font-semibold text-content-primary">{t('ekyc.dinhDanh.anhTieuDe')}</h2>
          <p className="text-xs text-content-secondary">{t('ekyc.dinhDanh.anhMo')}</p>
        </div>

        <ul className="space-y-3">
          {o.map((x) => (
            <OChonAnh key={x.vaiTro} phienId={phien.id} o={x} daCo={anh[x.vaiTro]} onCoAnh={onCoAnh} />
          ))}
        </ul>

        {nop.isError && <p role="alert" className="text-sm text-danger-700">{dichLoi(nop.error)}</p>}
        <div className="flex flex-wrap items-center gap-3 border-t border-default pt-4">
          <Button
            variant="primary"
            size="md"
            disabled={thieu > 0 || nop.isPending}
            onClick={() => nop.mutate(undefined, { onSuccess: onNop })}
          >
            {nop.isPending ? t('ekyc.dinhDanh.dangNop') : t('ekyc.dinhDanh.nopNut')}
          </Button>
          <Button variant="ghost" size="md" disabled={nop.isPending} onClick={onHoiHuy}>
            {t('ekyc.dinhDanh.huyNut')}
          </Button>
          {/* Nói còn thiếu bao nhiêu, thay vì để nút xám mà không ai biết vì sao. */}
          {thieu > 0 && <span className="text-xs text-content-secondary">{t('ekyc.dinhDanh.conThieu', { so: thieu })}</span>}
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Một ô ảnh. Mỗi ô một mutation riêng, nên ba ảnh tải song song được và lỗi của ô này không xoá trạng thái ô kia.
 *
 * Lỗi đến từ cổng kiểm của server và **đã nói cách xử lý** ("Đưa máy lại gần hơn…"), nên hiện nguyên văn ngay
 * dưới ô, không gói lại thành một câu chung. Dấu "đã nhận" đi theo lượt tải **của chính ảnh đang xem**: chụp lại mà
 * bị từ chối thì ảnh mới không được mang nhãn "đã nhận" của ảnh trước.
 */
function OChonAnh({
  phienId,
  o,
  daCo,
  onCoAnh,
}: {
  phienId: string;
  o: OAnh;
  daCo: AnhDaTai | undefined;
  onCoAnh: (a: AnhDaTai) => void;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const tai = useTaiAnh(phienId);
  const ten = t(o.ten);

  return (
    <li className="space-y-2 rounded-lg border border-default p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium text-content-primary">{ten}</p>
        {!daCo && <span className="text-xs text-content-muted">{t('ekyc.dinhDanh.batBuoc')}</span>}
      </div>
      <ChupAnh
        ten={ten}
        loai={o.vaiTro === 'SELFIE' ? 'chan-dung' : 'giay-to'}
        camera={o.camera}
        dangGui={tai.isPending}
        daNhan={tai.isSuccess ? { rong: tai.data.width, cao: tai.data.height } : null}
        onChon={(tep) => tai.mutate({ vaiTro: o.vaiTro, tep }, { onSuccess: onCoAnh })}
      />
      {tai.isError && <p role="alert" className="text-xs text-danger-700">{dichLoi(tai.error)}</p>}
    </li>
  );
}

function HopHuy({
  phienId,
  open,
  onClose,
  onDaHuy,
}: {
  phienId: string;
  open: boolean;
  onClose: () => void;
  onDaHuy: () => void;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const { huy } = useKetThucPhien(phienId);
  return (
    <HopXacNhanThaoTac
      open={open}
      tieuDe={t('ekyc.dinhDanh.huyTieuDe')}
      than={t('ekyc.dinhDanh.huyThan')}
      nhanXacNhan={t('ekyc.dinhDanh.huyXacNhan')}
      nguyHiem
      dangChay={huy.isPending}
      loi={huy.isError ? dichLoi(huy.error) : null}
      onClose={onClose}
      onXacNhan={() => huy.mutate(undefined, { onSuccess: onDaHuy })}
    />
  );
}

/** Chọn ứng dụng và quy trình trước khi mở phiên. Đã mở phiên thì khoá: bộ ảnh đã chụp theo quy trình ấy. */
function ChonQuyTrinh({
  ungDungId,
  onUngDung,
  quyTrinhId,
  onQuyTrinh,
  dsUngDung,
  dsQuyTrinh,
  hienQuyTrinh,
}: {
  ungDungId: string | null;
  onUngDung: (id: string) => void;
  quyTrinhId: string | null;
  onQuyTrinh: (id: string) => void;
  dsUngDung: UngDung[];
  dsQuyTrinh: QuyTrinh[];
  hienQuyTrinh: boolean;
}) {
  const { t } = useT();
  return (
    <Card>
      <CardBody className="grid gap-4 sm:grid-cols-2">
        <Field label={t('ekyc.dinhDanh.ungDung')}>
          {(p) => (
            <Select<string>
              id={p.id}
              className="w-full"
              value={ungDungId ?? undefined}
              onChange={onUngDung}
              options={dsUngDung.map((u) => ({ value: u.id, label: u.ten }))}
            />
          )}
        </Field>
        {hienQuyTrinh && (
          <Field label={t('ekyc.dinhDanh.quyTrinh')} hint={dsQuyTrinh.length === 0 ? t('ekyc.dinhDanh.quyTrinhMacDinhHeThong') : undefined}>
            {(p) => (
              <Select<string>
                id={p.id}
                className="w-full"
                value={quyTrinhId ?? undefined}
                onChange={onQuyTrinh}
                disabled={dsQuyTrinh.length === 0}
                options={dsQuyTrinh.map((q) => ({
                  value: q.id,
                  label: q.macDinh ? t('ekyc.dinhDanh.quyTrinhMacDinh', { ten: q.ten }) : q.ten,
                }))}
              />
            )}
          </Field>
        )}
      </CardBody>
    </Card>
  );
}
